import {
  IReportCriteriaFiltersSchemaValues,
  IReportTemplateFilters,
} from "@ryde/shared";

import { IDashboardCard } from "./dynamic-dashboard.models";
import {
  getSystemCardDefinition,
  isLegacyCard,
} from "./dynamic-dashboard.system-cards.utils";

/** The analytic reports' filter config, keyed by report id — see `useDashboardFilterSchema`. */
export type TSchemaByReport = Record<number, IReportTemplateFilters>;
export interface IReportSchemaState {
  byReport: TSchemaByReport;
  isSettled: boolean;
  failedReportIds: number[];
}

/**
 * The filter keys ONE card can be narrowed by, or `undefined` when that cannot be
 * answered. A system card declares its set outright; a custom card honours
 * exactly the filters its own report declares.
 *
 * `undefined` is deliberately distinct from `[]`. "This card supports nothing"
 * and "nobody knows what this card supports" look identical downstream but must
 * not be treated alike: callers that only hide things may conflate them, callers
 * that DELETE user state must not.
 *
 * A custom card whose report is absent from `byReport` is the interesting case,
 * and it splits three ways. Still loading, or that report failed: unknown, so the
 * card holds deletion off. Settled and never failed: the report simply isn't on
 * this user's menu — deleted, or not permitted — which is affirmative enough. Such
 * a card vouches for nothing rather than freezing the prune for the whole bar,
 * since it cannot render its own data either.
 */
const getCardHonourableKeys = ({
  card,
  schemaState,
}: {
  card: IDashboardCard;
  schemaState: IReportSchemaState;
}): string[] | undefined => {
  if (isLegacyCard(card)) {
    const definition = getSystemCardDefinition(card.legacyCardName);

    // A definition with no `supportedFilters` is a card that isn't filterable
    // yet — knowably nothing, as opposed to unknowable. Note this needs no report
    // fetch at all, so a system card resolves even while the menu is still
    // loading, or when an unrelated report has failed.
    return definition ? (definition.supportedFilters ?? []) : undefined;
  }

  if (typeof card.reportId !== "number") {
    return undefined;
  }

  const reportSchema = schemaState.byReport[card.reportId];

  if (reportSchema) {
    return Object.keys(reportSchema);
  }

  return !schemaState.isSettled ||
    schemaState.failedReportIds.includes(card.reportId)
    ? undefined
    : [];
};

/**
 * Every key any placed card can honour, or `undefined` when a single card's set
 * is unknown.
 *
 * The union and the safe-to-delete question are answered together on purpose: a
 * caller cannot hold the keys without having passed the gate, so there is no way
 * to prune against a union assembled from incomplete information.
 *
 * Note this is NOT intersected with the reports' filter config, the way the offer
 * list is. The add list needs widget config to render a chip; deleting needs only
 * to know that nothing honours the key. Intersecting here would conflate "no card
 * supports this" with "no loaded report can draw a widget for it", and would drop
 * a system card's `sites` because the report that happened to declare `sites`
 * failed to load — the card supports it regardless of who can draw it.
 */
export const getHonourableKeys = ({
  cards,
  schemaState,
}: {
  cards: IDashboardCard[];
  schemaState: IReportSchemaState;
}): string[] | undefined => {
  const perCard = cards.map(card =>
    getCardHonourableKeys({ card, schemaState }),
  );

  return perCard.some(keys => keys === undefined)
    ? undefined
    : [...new Set(perCard.flat() as string[])];
};

/**
 * The filters the bar is worth offering, out of the union it collects across
 * every analytic report. A key no card on the dashboard can honour would apply
 * silently and change nothing, which reads as a broken bar rather than as an
 * unsupported filter.
 *
 * EVERY placed card is asked what it can honour, custom cards included — which is
 * why the schema is kept per report id rather than pre-merged. Merging first
 * would let one custom card vouch for every filter any analytic report declares,
 * and the bar would be back to offering keys that card's own report never had.
 *
 * Intersected with `schema` because an add-list entry has to render a widget, and
 * the widget config is what `schema` holds.
 *
 * A card whose set is unknown contributes nothing here, and the list stays usable
 * while the rest of the menu loads. That is the safe direction for an offer list:
 * a key is merely absent, and returns as soon as the card resolves — as it also
 * does the moment a card supporting it is placed, since this is derived from the
 * current cards on every render and never stored.
 */
export const pickOfferableFilters = ({
  cards,
  schema,
  schemaState,
}: {
  cards: IDashboardCard[];
  schema: IReportTemplateFilters;
  schemaState: IReportSchemaState;
}): IReportTemplateFilters => {
  const honourable = new Set(
    cards.flatMap(card => getCardHonourableKeys({ card, schemaState }) ?? []),
  );

  return Object.fromEntries(
    Object.entries(schema).filter(([key]) => honourable.has(key)),
  );
};

/**
 * The filter values worth keeping: those whose key is in `keys`. Pure, and kept
 * out of the prune hook because it is the step that actually discards a user's
 * filters — the one piece of this feature that most deserves a test.
 */
export const keepFiltersWithKeys = ({
  filters,
  keys,
}: {
  filters: IReportCriteriaFiltersSchemaValues;
  keys: string[];
}): IReportCriteriaFiltersSchemaValues => {
  const kept = new Set(keys);

  return Object.fromEntries(
    Object.entries(filters).filter(([key]) => kept.has(key)),
  );
};
